import type { QRCodeConfigResponse } from '../../../types/api.types'
import { DEFAULT_PRINTER_PORT } from '../constants/adminConstants'

export interface ProvisioningPayload {
  mac_address: string
  property_id: number
  // Absent on devices that sit on no table — every non-restaurant vertical.
  table?: number
  organization_id: number
  team_id?: number
  user_id?: number
  ssid?: string
  password?: string
  device_name?: string
  comm_mode?: string
  mode?: 'cloud' | 'local'
  local_ip?: string
  local_port?: number
  printer_ip?: string
  printer_port?: number
}

export interface ProvisioningConfig {
  ssid: string
  password: string
  device_name: string
  comm_mode: string
  mode: string
  local_ip: string
  local_port: number
  // Restaurant only — the kitchen printer the device prints tickets to.
  printer_ip?: string
  printer_port?: number
}

function normalizeServerMode(raw: string | undefined | null): 'cloud' | 'local' {
  const m = String(raw ?? '').trim().toLowerCase()
  if (m === 'local') return 'local'
  return 'cloud'
}

export function isValidMAC(mac: string): boolean {
  return /^([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$/.test(mac) || /^[0-9A-Fa-f]{12}$/.test(mac)
}

export function generateRandomMAC(): string {
  const hex = () => Math.floor(Math.random() * 256).toString(16).padStart(2, '0').toUpperCase()
  return `${hex()}:${hex()}:${hex()}:${hex()}:${hex()}:${hex()}`
}

export function buildProvisioningPayload(
  deviceData: QRCodeConfigResponse,
  config: ProvisioningConfig
): ProvisioningPayload {
  let macAddress = deviceData.mac_address
  const isUDP = config.comm_mode === 'udp' || config.comm_mode === 'UDP'
  if (isUDP && !isValidMAC(macAddress)) {
    macAddress = generateRandomMAC()
  }

  // No invented placement. Flashing a device with a guessed property or table sends
  // its traffic to someone else's restaurant, and the wrongness only shows up later
  // as orders printing on the wrong table — fail here instead.
  if (!deviceData.property_id) {
    throw new Error('This device has no property. Close this and reopen setup from the Devices tab.')
  }

  const payload: ProvisioningPayload = {
    mac_address: macAddress,
    property_id: deviceData.property_id,
    organization_id: deviceData.organization_id,
  }

  // Omitted rather than defaulted: a device with no table has no table. And the
  // firmware needs the manager-facing number, never the row id — see QRCodeConfigResponse.
  if (deviceData.table_number != null) payload.table = deviceData.table_number
  if (deviceData.team_id) payload.team_id = deviceData.team_id
  if (deviceData.user_id) payload.user_id = deviceData.user_id
  if (config.ssid) payload.ssid = config.ssid
  if (config.password) payload.password = config.password

  payload.device_name = config.device_name?.trim() || 'QuickComm'
  payload.comm_mode = config.comm_mode || 'ai'
  payload.mode = normalizeServerMode(config.mode)

  if (payload.mode === 'local') {
    payload.local_ip = (config.local_ip ?? '').trim()
    payload.local_port =
      typeof config.local_port === 'number' && !Number.isNaN(config.local_port)
        ? config.local_port
        : 8000
  }

  // Only restaurant setups fill this in; without an IP the firmware has nothing
  // to print to, so a bare port is meaningless and both are left off.
  const printerIp = (config.printer_ip ?? '').trim()
  if (printerIp) {
    payload.printer_ip = printerIp
    payload.printer_port =
      typeof config.printer_port === 'number' && !Number.isNaN(config.printer_port)
        ? config.printer_port
        : DEFAULT_PRINTER_PORT
  }

  return payload
}
